import React, { useEffect, useContext, useRef, useState } from 'react'
import { Box, Checkbox, Flex, Label, Text } from 'pcln-design-system'
import { useForm } from 'react-hook-form'
import { Calendar } from '@pcln/date-picker'
import { pathOr } from 'ramda'
import CONFIG from 'isomorphic-config'
import useTabGroup from '@/hooks/useTabGroup'
import DateField from '@/components/DateField'
import TimeSelect from '@/components/TimeSelect/TimeSelect'
import TypeAhead from '@/components/TypeAhead'
import SearchFormButton from '@/components/SearchFormButton'
import CalendarWrapperBox from '@/components/CalenderWrapperBox.styled'
import ShadowEffect from '@/components/ShadowEffect'
import { PopoverContainer } from '@/components/Popover'
import {
  addFormattedDays,
  formatDate,
  getPackagesDateRange
} from '@/shared-utils/date-helper'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import BundleAndSave from '@/components/BundleAndSave/BundleAndSave'
import TravelerSelection from '@/components/TravelerSelection/TravelerSelection'
import { VP_DISCLAIMER } from '@/components/TravelerSelection/constants'
import { ANALYTICS_CATEGORY_HOME } from '@/constants/analytics'
import analytics from '@/shared-utils/analytics'
import {
  retrieveManifest,
  insertPrefetchLinks
} from '@/shared-utils/prefetchApp'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  ADD_PRODUCT_OPTIONS,
  CALENDAR_POPOVER_SELECTOR,
  STAY
} from '../constants'
import { CAR_FORM_STATE_TYPE } from '../types'
import { fields, initialState } from './utils'
import searchFormSchema from './validation'

function SubmitButtonContainer({ watchTripType }: { watchTripType: string }) {
  const { webstats, customer } = useContext(BootstrapDataContext)
  return (
    <Box width={[1, null, 1 / 2]} pl={[0, null, 2]}>
      <SearchFormButton
        buttonText="Find Your Car"
        data-testid="HOME_RENTALCARS_SUBMIT_BUTTON"
        data-rudder-event
        data-sub-type="search"
        data-source="homepage"
        data-parent-product="DRIVE"
        data-child-product={watchTripType.split(':')?.[1]}
        data-cguid={webstats.clientGUID}
        data-email-address={customer.emailAddress}
      />
      <Box mt={2}>
        <Text color="text" textAlign="center" fontSize={0}>
          Book a car with free cancellation for flexibility
        </Text>
      </Box>
    </Box>
  )
}

type FormType = {
  onSubmit: (data: CAR_FORM_STATE_TYPE) => void
}

export default function Form({ onSubmit }: FormType) {
  const { isMobile } = useContext(BootstrapDataContext)
  const { registerTabElement, focusNextElement } = useTabGroup()
  const [isStartCalendarOpen, setIsStartCalendarOpen] = useState(false)
  const [isEndCalendarOpen, setIsEndCalendarOpen] = useState(false)
  const [isHotelCalendarOpen, setIsHotelCalendarOpen] = useState(false)
  const [dsrManifestFetched, setDSRManifestFetched] = useState(false)

  const {
    clearErrors,
    handleSubmit,
    register,
    setValue,
    trigger,
    unregister,
    watch,
    formState: { errors }
  } = useForm<CAR_FORM_STATE_TYPE>({
    defaultValues: initialState,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange'
  })

  useEffect(() => {
    fields.forEach(name => {
      register(name)
    })
    return () => {
      fields.forEach(name => {
        unregister(name)
      })
    }
  }, [register, unregister])

  const {
    adultCount: watchAdultCount,
    childrenAges: watchChildrenAges,
    childrenCount: watchChildrenCount,
    endDate: watchEndDate,
    endTime: watchEndTime,
    endLocation: watchEndLocation,
    hotelEndDate: watchHotelEndDate,
    hotelStartDate: watchHotelStartDate,
    oneWay: watchOneWay,
    roomCount: watchRoomCount,
    startDate: watchStartDate,
    startTime: watchStartTime,
    startLocation: watchStartLocation,
    tripType: watchTripType
  } = watch()

  useEffect(() => {
    trigger('childrenAges')
  }, [
    trigger,
    watchStartLocation,
    watchEndLocation,
    watchChildrenAges,
    watchAdultCount,
    watchChildrenCount
  ])

  useEffect(() => {
    clearErrors('endDate')
  }, [clearErrors, watchEndDate])

  useEffect(() => {
    clearErrors('endLocation')
  }, [clearErrors, watchEndLocation])

  useEffect(() => {
    clearErrors('hotelStartDate')
  }, [clearErrors, watchHotelStartDate])

  useEffect(() => {
    clearErrors('roomCount')
  }, [clearErrors, watchRoomCount])

  useEffect(() => {
    clearErrors('startDate')
  }, [clearErrors, watchStartDate])

  useEffect(() => {
    clearErrors('startLocation')
  }, [clearErrors, watchStartLocation])

  useEffect(() => {
    trigger('roomCount')
  }, [
    trigger,
    watchChildrenCount,
    watchRoomCount,
    watchAdultCount,
    watchTripType
  ])

  const hasHotel = watchTripType.includes(STAY)
  const pickupCalendarInput = useRef<HTMLInputElement | null>(null)
  const dropoffCalendarInput = useRef<HTMLInputElement | null>(null)

  const handleFocus = async (formTarget: React.FocusEvent) => {
    const isNotAddHotel = formTarget.target.id !== 'add-a-hotel-checkbox'
    const isNotUndefined = formTarget.target.id !== ''
    const fetchDsrManifest =
      !dsrManifestFetched && isNotAddHotel && isNotUndefined && !hasHotel
    if (fetchDsrManifest) {
      const dsrManifest = CONFIG.client.prefetch.dsr
      const links = await retrieveManifest(dsrManifest)
      insertPrefetchLinks(links)
      setDSRManifestFetched(true)
    }
  }

  return (
    <form
      aria-label="rental-car-search-form"
      data-testid="form"
      onFocus={handleFocus}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Flex flexWrap="wrap" mx={-2}>
        {!hasHotel && (
          <ShadowEffect
            forProperty="startLocation"
            width={watchOneWay ? [1, null, 1 / 2] : 1}
            px={2}
            mb={[0, null, 2]}
          >
            {({ disableShadowState }: { disableShadowState: () => void }) => (
              <TypeAhead
                searchProduct="cars"
                errors={errors}
                defaultSelectedItem={watchStartLocation}
                disableShadowState={disableShadowState}
                focusNextElement={focusNextElement}
                label="City, Airport or Address"
                placeholder="City, Airport or Address"
                ref={registerTabElement}
                searchKey="startLocation"
                setValue={setValue}
              />
            )}
          </ShadowEffect>
        )}
        {(hasHotel || watchOneWay) && (
          <ShadowEffect
            forProperty="endLocation"
            px={2}
            width={!hasHotel && watchOneWay ? [1, null, 1 / 2] : 1}
            mb={[-2, null, 2]}
          >
            {({ disableShadowState }: { disableShadowState: () => void }) => (
              <TypeAhead
                searchProduct={hasHotel ? 'packagesDestination' : 'cars'}
                defaultSelectedItem={watchEndLocation}
                disableShadowState={disableShadowState}
                errors={errors}
                focusNextElement={focusNextElement}
                label="City, Airport or Address"
                placeholder="City, Airport or Address"
                ref={registerTabElement}
                searchKey="endLocation"
                setValue={setValue}
              />
            )}
          </ShadowEffect>
        )}
      </Flex>
      {!hasHotel && (
        <Box width={[1, null, 'auto']} mb={-1}>
          <Label htmlFor="oneWay" fontSize={[0, null, '10px']}>
            <Checkbox
              id="oneWay"
              {...register('oneWay')}
              data-testid="oneWay"
              onClick={() => {
                analytics.fireEvent({
                  action: 'Field Click',
                  category: ANALYTICS_CATEGORY_HOME,
                  label: 'RentalCars - oneway click'
                })
              }}
            />
            Return car to a different location
          </Label>
        </Box>
      )}
      <Flex
        alignItems="center"
        flexWrap={['wrap', null, null, null, 'nowrap']}
        mt={2}
        mx={-2}
        mb={[-2, null, 0]}
      >
        {!hasHotel && (
          <>
            <CalendarWrapperBox
              mt={[2, null, 0]}
              px={2}
              mb={[0, null, 3]}
              width={[1 / 2, null, null, null, 1 / 4]}
            >
              <ShadowEffect forProperty="startDate">
                {({
                  disableShadowState
                }: {
                  disableShadowState: () => void
                }) => (
                  <PopoverContainer
                    data-calendar
                    domSelectorForVisuallyContainedElement={
                      CALENDAR_POPOVER_SELECTOR
                    }
                    key="startDate"
                    onDismiss={() => {
                      setIsStartCalendarOpen(false)
                      disableShadowState()
                    }}
                    open={isStartCalendarOpen}
                    position="left"
                  >
                    <DateField
                      aria-expanded={isStartCalendarOpen ? 'true' : 'false'}
                      data-testid="HOME_RENTALCARS_STARTDATE_INPUT"
                      error={errors.startDate?.message}
                      label="Pick-up"
                      id="startDate"
                      ref={pickupCalendarInput}
                      m={0}
                      name="startDate"
                      onFocus={() => {
                        setIsStartCalendarOpen(true)
                      }}
                      placeholder="Pick-up"
                      type="text"
                      value={formatDate(watchStartDate)}
                    />
                    <Box
                      boxShadowSize={!isMobile && 'overlay-lg'}
                      borderRadius={!isMobile && '2xl'}
                    >
                      <Calendar
                        allowSameDay
                        endDate={watchEndDate}
                        roundedCorners
                        monthWidthPx={350}
                        onChange={(
                          e: React.ChangeEvent,
                          { startDate }: { startDate: string }
                        ) => {
                          if (startDate) {
                            setValue('startDate', startDate, {
                              shouldDirty: true
                            })
                            setValue(
                              'endDate',
                              addFormattedDays(startDate, 1, 'yyyyMMdd'),
                              { shouldDirty: true }
                            )
                            setIsStartCalendarOpen(false)
                            disableShadowState()
                          }
                        }}
                        onDismiss={() => {
                          setIsStartCalendarOpen(false)
                          disableShadowState()
                        }}
                        scrollDate={watchStartDate}
                        selectTwoDates={false}
                        startDate={watchStartDate}
                        useDefaultFooter
                      />
                    </Box>
                  </PopoverContainer>
                )}
              </ShadowEffect>
            </CalendarWrapperBox>
            <Box
              px={2}
              mb={[2, null, 3]}
              width={[1 / 2, null, null, null, 1 / 4]}
            >
              <ShadowEffect forProperty="startTime">
                {({
                  disableShadowState
                }: {
                  disableShadowState: () => void
                }) => (
                  <TimeSelect
                    data-testid="HOME_RENTALCARS_STARTTIME_SELECT"
                    label="Pick-up Time"
                    name="startTime"
                    onChange={(e: React.ChangeEvent) => {
                      const endTime = pathOr('', ['currentTarget', 'value'], e)
                      setValue('startTime', endTime, { shouldDirty: true })
                      setValue('endTime', endTime, { shouldDirty: true })
                      disableShadowState()
                    }}
                    onDismiss={() => {
                      disableShadowState()
                    }}
                    value={watchStartTime}
                  />
                )}
              </ShadowEffect>
            </Box>
            <Box px={2} mb={3} width={[1 / 2, null, null, null, 1 / 4]}>
              <ShadowEffect forProperty="endDate">
                {({
                  disableShadowState
                }: {
                  disableShadowState: () => void
                }) => (
                  <PopoverContainer
                    data-calendar
                    domSelectorForVisuallyContainedElement={
                      CALENDAR_POPOVER_SELECTOR
                    }
                    key="endDate"
                    open={isEndCalendarOpen}
                    onDismiss={() => {
                      setIsEndCalendarOpen(false)
                      disableShadowState()
                    }}
                    position="left"
                  >
                    <DateField
                      aria-expanded={isEndCalendarOpen ? 'true' : 'false'}
                      data-testid="HOME_RENTALCARS_ENDDATE_INPUT"
                      error={errors.endDate?.message}
                      id="endDate"
                      ref={dropoffCalendarInput}
                      label="Drop-off"
                      m={0}
                      name="endDate"
                      onFocus={() => {
                        setIsEndCalendarOpen(true)
                      }}
                      placeholder="Drop-off"
                      type="text"
                      value={formatDate(watchEndDate)}
                    />
                    <CalendarWrapperBox
                      boxShadowSize={!isMobile && 'overlay-lg'}
                      borderRadius={!isMobile && '2xl'}
                    >
                      <Calendar
                        allowSameDay
                        endDate={watchEndDate}
                        roundedCorners
                        monthWidthPx={350}
                        onChange={(
                          e: React.ChangeEvent,
                          { startDate }: { startDate: string }
                        ) => {
                          if (startDate) {
                            setValue('endDate', startDate, {
                              shouldDirty: true
                            })
                            setIsEndCalendarOpen(false)
                            disableShadowState()
                          }
                        }}
                        onDismiss={() => {
                          setIsEndCalendarOpen(false)
                          disableShadowState()
                        }}
                        position="left"
                        scrollDate={watchEndDate}
                        selectTwoDates={false}
                        startDate={watchStartDate}
                        useDefaultFooter
                      />
                    </CalendarWrapperBox>
                  </PopoverContainer>
                )}
              </ShadowEffect>
            </Box>
            <Box
              px={2}
              mb={[4, null, 3]}
              width={[1 / 2, null, null, null, 1 / 4]}
            >
              <ShadowEffect forProperty="endTime">
                {({
                  disableShadowState
                }: {
                  disableShadowState: () => void
                }) => (
                  <TimeSelect
                    data-testid="HOME_RENTALCARS_ENDTIME_SELECT"
                    label="Drop-off Time"
                    name="endTime"
                    onChange={(e: React.ChangeEvent) => {
                      const endTime = pathOr('', ['currentTarget', 'value'], e)
                      setValue('endTime', endTime, { shouldDirty: true })
                      disableShadowState()
                    }}
                    onDismiss={() => {
                      disableShadowState()
                    }}
                    value={watchEndTime}
                  />
                )}
              </ShadowEffect>
            </Box>
          </>
        )}
        {hasHotel && (
          <>
            <Flex flexWrap="wrap" mb={[0, null, 3]} width={[1, null, 1 / 2]}>
              <CalendarWrapperBox id="rc-with-hotel-dates" px={2} width={1}>
                <ShadowEffect enabled={!isMobile} forProperty="startDate">
                  {({
                    disableShadowState
                  }: {
                    disableShadowState: () => void
                  }) => (
                    <PopoverContainer
                      data-calendar
                      domSelectorForVisuallyContainedElement={
                        CALENDAR_POPOVER_SELECTOR
                      }
                      open={isHotelCalendarOpen}
                      onDismiss={() => {
                        setIsHotelCalendarOpen(false)
                        disableShadowState()
                      }}
                    >
                      <DateField
                        aria-expanded={isHotelCalendarOpen ? 'true' : 'false'}
                        error={
                          errors.hotelStartDate?.message ||
                          errors.hotelEndDate?.message ||
                          ''
                        }
                        label="Departing - Returning"
                        name="hotelDates"
                        onFocus={() => {
                          setIsHotelCalendarOpen(true)
                        }}
                        value={getPackagesDateRange({
                          startDate: watchHotelStartDate,
                          endDate: watchHotelEndDate
                        })}
                      />
                      <Box
                        boxShadowSize={!isMobile && 'overlay-lg'}
                        borderRadius={!isMobile && '2xl'}
                      >
                        <Calendar
                          endDate={watchHotelEndDate}
                          roundedCorners
                          monthWidthPx={350}
                          onChange={(
                            e: React.ChangeEvent,
                            {
                              startDate,
                              endDate
                            }: { startDate: string; endDate: string }
                          ) => {
                            if (startDate && endDate) {
                              setIsHotelCalendarOpen(false)
                              disableShadowState()
                            }
                            setValue('hotelStartDate', startDate || '', {
                              shouldDirty: true
                            })
                            setValue('hotelEndDate', endDate || '', {
                              shouldDirty: true
                            })
                          }}
                          onDismiss={() => {
                            setIsHotelCalendarOpen(false)
                            disableShadowState()
                          }}
                          startDate={watchHotelStartDate}
                          useDefaultFooter
                        />
                      </Box>
                    </PopoverContainer>
                  )}
                </ShadowEffect>
              </CalendarWrapperBox>
            </Flex>
            <Flex
              flexWrap="wrap"
              mb={[4, null, 3]}
              px={2}
              width={[1, null, 1 / 2]}
            >
              <ShadowEffect
                alternativeBlurHandling
                forProperty="travelers-selection-readonly-input"
              >
                {({
                  disableShadowState
                }: {
                  disableShadowState: () => void
                }) => (
                  <TravelerSelection
                    adultCount={watchAdultCount}
                    childrenAges={watchChildrenAges}
                    childrenCount={watchChildrenCount}
                    disableShadowState={disableShadowState}
                    disclaimer={VP_DISCLAIMER}
                    error={
                      errors?.childrenAges?.message ||
                      errors?.roomCount?.message ||
                      ''
                    }
                    infantCount={0}
                    isMobile={isMobile}
                    onDoneButtonClick={disableShadowState}
                    roomCount={watchRoomCount}
                    setValue={setValue}
                    showChildAgeSelectors
                    childAgeSelectors={{
                      optionsText: {
                        infant: 'Infant',
                        oneYear: '1 Year Old'
                      }
                    }}
                    showInfantMenuItem={false}
                    showRoomMenuItem
                    showSubLabel={false}
                    showYouthMenuItem={false}
                    youthCount={0}
                  />
                )}
              </ShadowEffect>
            </Flex>
          </>
        )}
      </Flex>
      <Flex flexDirection={['column', null, 'row']}>
        <Box
          width={[1, null, 1 / 2]}
          height="56px"
          pr={[0, null, 2]}
          mb={[3, null, 0]}
          mt={[-1, null, 0]}
        >
          <BundleAndSave
            category={ANALYTICS_CATEGORY_HOME}
            label="Bundle + Save"
            productOptions={ADD_PRODUCT_OPTIONS}
            setValue={setValue}
            tripType={watchTripType}
          />
        </Box>
        <SubmitButtonContainer watchTripType={watchTripType} />
      </Flex>
    </form>
  )
}
